import { AnimatePresence, LazyMotion, m } from "framer-motion";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import { ProgressBarExpansion } from "../../components/animation/pecentageBar";

import { Card } from "../../components/Card";
import { Flex } from "../../components/Grid";
import { Toggle } from "../../components/Toggle";
import { Footnote, Title2 } from "../../components/Typography";
import { xSmallScreen } from "../../lib/mediaQuery";
import { DropTimeText } from "./dropTime";

const loadFeatures = () =>
  import("../../framer-motion.js").then((res) => res.default);

interface SoldIndicatorProps {
  className?: string;
  totalKeys: number;
  soldKeys: number;
  isEnded?: boolean;
  isFree?: boolean;
}

export const SoldIndicator: React.FC<SoldIndicatorProps> = ({
  className,
  totalKeys,
  soldKeys,
  isEnded,
  isFree,
}) => {
  const percentageSold = (soldKeys / totalKeys) * 100;
  const isSoldOut = soldKeys === totalKeys;
  const [showCount, setShowCount] = useState<boolean>(!isSoldOut);

  return (
    <DropHighlightContainer>
      <DropContainer
        onClick={() => setShowCount(!showCount)}
        className={className}
        css={`
          display: flex;
          flex-direction: column;
          position: relative;
          cursor: "pointer";
        `}
      >
        {soldKeys > 0 && (
          <Flex direction="row">
            <Footnote
              css={`
                color: rgb(var(--grey-mid));
              `}
            >
              {isSoldOut ? "Sold Out" : isEnded ? "Sale Ended" : "Sale is Live"}
            </Footnote>
          </Flex>
        )}
        {soldKeys > 0 && (
          <div
            css={`
              position: absolute;
              top: -5px;
              right: 0;
            `}
          >
            <Toggle
              toggleCallback={() => setShowCount(!showCount)}
              parentState={showCount}
              options={["#", "%"]}
              variant={"glass"}
              size={"small"}
            />
          </div>
        )}
        <Flex direction="row">
          <Flex direction="column" width={"100%"}>
            <Title2
              css={`
                position: relative;
                color: rgb(var(--white-light));
                font-variant-numeric: tabular-nums;
              `}
            >
              <LazyMotion features={loadFeatures}>
                <AnimatePresence>
                  <m.div
                    key={showCount ? "sold_keys" : "sold_pct"}
                    exit={{ opacity: 0, position: "absolute" }}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      ease: "easeOut",
                      duration: 0.2,
                    }}
                  >
                    {isEnded && soldKeys === 0
                      ? "Sale Ended"
                      : soldKeys === 0
                      ? "Sale is Live"
                      : isFree
                      ? showCount
                        ? `${soldKeys} Claimed`
                        : `${percentageSold.toFixed()}% Claimed`
                      : showCount
                      ? `${soldKeys} Sold`
                      : `${percentageSold.toFixed()}% Sold`}
                  </m.div>
                </AnimatePresence>
              </LazyMotion>
            </Title2>
          </Flex>
        </Flex>
        <Flex direction="row">
          <Flex direction="column" width={"100%"}>
            <PercentageBar percentage={percentageSold} />
          </Flex>
        </Flex>
      </DropContainer>
    </DropHighlightContainer>
  );
};

export const DropHighlightContainer = styled(Card).attrs({
  shadow: false,
})`
  flex: 1;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: var(--spacing-sm) var(--spacing-xs);
  margin: 0;
  ${xSmallScreen(css`
    height: 87px;
  `)}
  ${DropTimeText} {
    text-align: start;
  }
`;

const DropContainer = styled.div`
  opacity: 1;
  border-radius: var(--spacing-xxs);
  transition: background-color var(--transition-speed-normal) ease;
  min-width: 170px;
`;

interface PercentageBarProps {
  percentage?: number;
}

const PercentageBar = ({ percentage }: PercentageBarProps) => (
  <PercentageContainer>
    <PercentageIndicator percentage={percentage} />
  </PercentageContainer>
);

const PercentageContainer = styled.div`
  height: var(--spacing-xs);
  width: 100%;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 8px;
`;
const PercentageIndicator = styled.div<{ percentage: number }>`
  width: ${(p) => p.percentage}%;
  ${(p) => ProgressBarExpansion(p.percentage)};
  min-width: calc(var(--spacing-xs) - 2px);
  max-width: 100%;
  height: calc(var(--spacing-xs));

  background: #fff;
  border-radius: inherit;
`;
